import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { authFetch, formatApiError } from '../utils/api';
import { ArrowLeft, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const TXT = {
  en: {
    back: 'Home',
    verifying: 'Verifying your email...',
    success: 'Email verified successfully',
    successDesc: 'Your account is now fully activated. You can continue using Heliophant.',
    failure: 'Verification failed',
    failureDesc: 'This verification link is invalid or has already been used.',
    missing: 'Missing verification token',
    missingDesc: 'Please use the link from your email.',
    cta: 'Go to Home',
    resend: 'Resend verification email',
    resendSent: 'A new verification email has been sent if your account is unverified.',
  },
  el: {
    back: 'Αρχική',
    verifying: 'Επαλήθευση email...',
    success: 'Το email επαληθεύτηκε επιτυχώς',
    successDesc: 'Ο λογαριασμός σας ενεργοποιήθηκε πλήρως. Μπορείτε να συνεχίσετε να χρησιμοποιείτε το Heliophant.',
    failure: 'Η επαλήθευση απέτυχε',
    failureDesc: 'Ο σύνδεσμος επαλήθευσης δεν είναι έγκυρος ή έχει ήδη χρησιμοποιηθεί.',
    missing: 'Λείπει το token επαλήθευσης',
    missingDesc: 'Χρησιμοποιήστε τον σύνδεσμο από το email σας.',
    cta: 'Πίσω στην Αρχική',
    resend: 'Αποστολή νέου email επαλήθευσης',
    resendSent: 'Εστάλη νέο email επαλήθευσης εάν ο λογαριασμός σας είναι μη επαληθευμένος.',
  }
};

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { language } = useLanguage();
  const { checkAuth, user } = useAuth();
  const T = TXT[language] || TXT.en;

  const [status, setStatus] = useState(token ? 'verifying' : 'missing');
  const [errorMsg, setErrorMsg] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNote, setResendNote] = useState('');

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const res = await authFetch('/api/auth/verify-email', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });
        if (!active) return;
        if (res.ok) {
          setStatus('success');
          // Refresh auth so the navbar/banner reflect verified status
          checkAuth?.();
        } else {
          const data = await res.json().catch(() => ({}));
          if (data && data.detail) setErrorMsg(formatApiError(data.detail));
          setStatus('failure');
        }
      } catch (e) {
        if (active) { setErrorMsg(String(e)); setStatus('failure'); }
      }
    })();
    return () => { active = false; };
  }, [token, checkAuth]);

  const handleResend = async () => {
    if (!user?.email) return;
    setResendBusy(true);
    setResendNote('');
    try {
      await authFetch('/api/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email: user.email }),
      });
      setResendNote(T.resendSent);
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050811] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center text-white/60 hover:text-white text-sm mb-8 transition-colors" data-testid="verify-back">
          <ArrowLeft size={16} className="mr-2" /> {T.back}
        </Link>

        <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 size={48} className="text-cyan-400 mx-auto mb-4 animate-spin" />
              <h1 data-testid="verify-status" className="text-2xl font-bold text-white">{T.verifying}</h1>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 size={56} className="text-green-400 mx-auto mb-4" />
              <h1 data-testid="verify-status" className="text-2xl font-bold text-white mb-3">{T.success}</h1>
              <p className="text-white/70 text-sm mb-6">{T.successDesc}</p>
              <Button asChild className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600" size="lg">
                <Link to="/" data-testid="verify-home">{T.cta}</Link>
              </Button>
            </>
          )}

          {status === 'failure' && (
            <>
              <AlertCircle size={56} className="text-red-400 mx-auto mb-4" />
              <h1 data-testid="verify-status" className="text-2xl font-bold text-white mb-3">{T.failure}</h1>
              <p className="text-white/70 text-sm mb-6">{errorMsg || T.failureDesc}</p>
              {user?.email && !user?.email_verified && (
                <>
                  <Button onClick={handleResend} disabled={resendBusy} variant="outline" className="w-full mb-3" data-testid="verify-resend">
                    {resendBusy ? '...' : T.resend}
                  </Button>
                  {resendNote && <p className="text-cyan-400 text-xs mb-3">{resendNote}</p>}
                </>
              )}
              <Button asChild className="w-full bg-white text-black hover:bg-white/90" size="lg">
                <Link to="/">{T.cta}</Link>
              </Button>
            </>
          )}

          {status === 'missing' && (
            <>
              <AlertCircle size={56} className="text-yellow-400 mx-auto mb-4" />
              <h1 data-testid="verify-status" className="text-2xl font-bold text-white mb-3">{T.missing}</h1>
              <p className="text-white/70 text-sm mb-6">{T.missingDesc}</p>
              <Button asChild className="w-full bg-white text-black hover:bg-white/90" size="lg">
                <Link to="/">{T.cta}</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
